import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
  onClose: () => void;
}

const COMMANDS = [
  { id: 'feed', label: 'Go to Feed', path: '/' },
  { id: 'search', label: 'Search Insights', path: '/search' },
  { id: 'sources', label: 'Manage Sources', path: '/sources' },
  { id: 'review', label: 'Review Queue', path: '/review' },
];

export function CommandPalette({ onClose }: Props) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const filtered = COMMANDS.filter((cmd) =>
    cmd.label.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      navigate(filtered[selectedIndex].path);
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] md:pt-[20vh]"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Palette */}
      <div
        className="relative w-full max-w-lg mx-3 md:mx-0 bg-obsidian-bg-secondary border border-obsidian-border rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a command..."
          className="w-full px-4 py-3 bg-transparent text-obsidian-text placeholder-obsidian-text-faint border-b border-obsidian-border focus:outline-none"
        />

        <div className="max-h-64 overflow-auto py-2">
          {filtered.map((cmd, i) => (
            <button
              key={cmd.id}
              className={`w-full px-4 py-3 md:py-2 text-left text-sm transition-colors ${
                i === selectedIndex
                  ? 'bg-obsidian-surface text-obsidian-text'
                  : 'text-obsidian-text-muted hover:bg-obsidian-bg-tertiary'
              }`}
              onClick={() => {
                navigate(cmd.path);
                onClose();
              }}
              onMouseEnter={() => setSelectedIndex(i)}
            >
              {cmd.label}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="px-4 py-2 text-sm text-obsidian-text-faint">
              No commands found
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
